"use client";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  useToast,
  VStack,
} from "@chakra-ui/react";
import Select from "react-select";

import PrequalFormRow from "./PrequalFormRow";
import { useEffect, useState } from "react";
import { useSearchParams, useRouter } from "next/navigation";
import { getUTMParam } from "@/utils/helpers";
import YesNoRadioGroup from "../dashboard/application/YesNoRadioGroup";
import { Controller, useForm } from "react-hook-form";
import {
  EVENT_LIBRARY,
  REQUIRED_FIELD,
  ROUTE_PATHS,
  SERVER_DATE_FORMAT,
} from "@/lib/constants";
import moment from "moment";
import PhoneNumberInput from "../forms/PhoneNumberInput";
import { GetTenantByCode } from "@/actions/common/GetTenantByCode";
import useAnalytics from "@/lib/hooks/useAnalytics";
import validations from "@/utils/validations";
import handleServerAction from "@/lib/hooks/handleServerAction";
import CurrencyInput from "react-currency-input-field";
import {
  PrequalificationNewForm,
  PrequalificationNewFormPayload,
} from "@/actions/onboarding/PrequalificationNewForm";
import DateOfBirthInput from "../forms/DateOfBirthInput";
import { isBrowser } from "@/utils/helpers";
import LocationSelectReactSelect from "./LocationSelectReactSelect";
import {
  TermsAndConditionsErrorToast,
  TermsAndConditionsModal,
} from "./TermsAndConditions";
import PrequalNotQualified from "./PrequalNotQualified";
import DuplicateEmailInlineAlert from "../landing/DuplicateEmailInlineAlert";

const placeHolderReleaseDate = moment()
  .subtract(1, "days")
  .format("YYYY-MM-DD");

const SectionHeader = ({
  title,
  description,
}: {
  title: string;
  description?: string;
}) => {
  return (
    <VStack alignItems="flex-start" w="100%" fontFamily="Inter">
      <Heading as="h3" fontSize="20px" fontWeight="700">
        {title}
      </Heading>
      <Heading as="h4" fontSize="14px" fontWeight="400" lineHeight="24px">
        {description}
      </Heading>
    </VStack>
  );
};

interface PrequalFormProps {
  isJailUser?: boolean;
  viaPathSsoUserName?: string;
  firstName?: string;
  lastName?: string;
}
const prefillValuesForTesting = false; //never ever commit this set to true. will probably remove once we get tablet out
const PrequalForm = ({
  isJailUser,
  viaPathSsoUserName,
  firstName,
  lastName,
}: PrequalFormProps) => {
  const router = useRouter();
  const toast = useToast();
  const searchParams = useSearchParams();
  const track = useAnalytics(["Posthog", "Google", "Meta"]);

  const [tenant, setTenant] = useState<any>();
  const [notQualified, setNotQualified] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isDuplicateEmailAlertShowing, setIsDuplicateEmailAlertShowing] =
    useState(false);
  const id = searchParams.get("id") || "";
  const eid = searchParams.get("eid") || "";
  const {
    handleSubmit,
    control,
    register,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<
    PrequalificationNewFormPayload & { DoYouHaveAnEmailAddress: boolean }
  >({
    defaultValues: {
      FullName:
        searchParams.get("fullName") ||
        (prefillValuesForTesting
          ? "John Doe"
          : viaPathSsoUserName
          ? `${firstName} ${lastName}`
          : ""),
      Email:
        searchParams.get("email") ||
        (prefillValuesForTesting
          ? "test" +
            Date()
              .replace(" ", "")
              .replace(/[^A-Za-z0-9]/g, "")
          : viaPathSsoUserName || ""),
      UserName: searchParams.get("userName") || "",
      PhoneNumber:
        searchParams.get("phoneNumber") ||
        (prefillValuesForTesting ? "1234567890" : ""),
      LocationId: searchParams.get("locationId")
        ? parseInt(searchParams.get("locationId") || "")
        : prefillValuesForTesting
        ? 14
        : undefined,
      HowDidYouHearAboutUs: prefillValuesForTesting ? "Other" : "",
      IsJailUserReleasedInNext6Months: prefillValuesForTesting ? "unsure" : "",
      IsUsCitizen: prefillValuesForTesting ? true : undefined,
      IsReceivingGovtBenefits: prefillValuesForTesting ? false : undefined,
      OwesSocialSecurityForOverpayments: prefillValuesForTesting
        ? false
        : undefined,
      HasPhysicalOrMentalHealthCondition: prefillValuesForTesting
        ? true
        : undefined,
      IsCurrentlyWorking: prefillValuesForTesting ? false : undefined,
      MaximumHoursWorkedPerWeek: prefillValuesForTesting ? "0" : "",
      HourlyWage: prefillValuesForTesting ? "0" : "",
      DateOfBirth: prefillValuesForTesting ? "06-07-08" : "",
      EstimatedReleaseDate: prefillValuesForTesting ? "06-07-08" : undefined,
      ExternalInmateId: prefillValuesForTesting ? "123456" : undefined,
    },
  });

  const submitDetails = async (payload: PrequalificationNewFormPayload) => {
    try {
      if (isJailUser && !termsAccepted && !prefillValuesForTesting) {
        return TermsAndConditionsErrorToast(toast);
      }
      const Age = moment().diff(moment(payload.DateOfBirth), "years");
      const MaximumHoursWorkedPerWeek = payload.IsCurrentlyWorking
        ? parseInt(payload.MaximumHoursWorkedPerWeek)
        : 0;
      const HourlyWage = payload.IsCurrentlyWorking
        ? parseFloat(payload.HourlyWage)
        : 0;

      const estimatedReleaseDate = payload.EstimatedReleaseDate
        ? moment(payload.EstimatedReleaseDate, SERVER_DATE_FORMAT)
        : undefined;

      const isWithinNextSixMonths = estimatedReleaseDate
        ? estimatedReleaseDate.isSameOrBefore(moment().add(6, "months"))
          ? "Yes"
          : "No"
        : payload.IsJailUserReleasedInNext6Months;

      const encryptedToken = searchParams.get("encryptedToken");
      try {
        var result = await handleServerAction(PrequalificationNewForm, {
          ...payload,
          Age: Age.toString(),
          TenantCode: eid ? eid : undefined,
          UtmSource: getUTMParam("utm_source"),
          UtmMedium: getUTMParam("utm_medium"),
          UtmCampaign: getUTMParam("utm_campaign"),
          SecureRegistrationLinkToken: encryptedToken,
          EstimatedReleaseDate:
            estimatedReleaseDate?.format(SERVER_DATE_FORMAT),
          MaximumHoursWorkedPerWeek,
          HourlyWage,
          IsJailUserReleasedInNext6Months: isWithinNextSixMonths,
          IsJailUser: Boolean(isJailUser),
        });
      } catch (err) {
        if (
          err.message.includes(
            "We aren't able to complete your pre-qualification"
          )
        ) {
          setIsDuplicateEmailAlertShowing(true);
        } else {
          toast({
            status: "error",
            title: "Error Occured",
            description: err.message,
          });
        }
        if (isBrowser()) {
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
        return;
      }

      const dq_reasons = [];

      if (!payload.IsUsCitizen) {
        dq_reasons.push("Not a US Citizen");
      }
      if (payload.IsReceivingGovtBenefits) {
        dq_reasons.push("Receiving Govt Benefits");
      }
      if (!payload.HasPhysicalOrMentalHealthCondition) {
        dq_reasons.push("No Physical/Mental Health Condition");
      }
      if (MaximumHoursWorkedPerWeek * HourlyWage * 4 > 1500) {
        dq_reasons.push("Income too high");
      }
      if (Age > 64) {
        dq_reasons.push("Age over 64");
      }

      const isPrequalified = dq_reasons.length === 0;

      track(EVENT_LIBRARY.SUBMITTED_PREQUAL, {
        prequalified: isPrequalified,
        dq_reasons: dq_reasons.reduce(
          (obj, key) => {
            obj[key] = true;
            return obj;
          },
          {} as {
            [key: string]: boolean;
          }
        ),
      });

      if (isPrequalified) {
        const queryString = new URLSearchParams({
          id,
          fullName: payload.FullName!,
          email: payload.Email!,
          encryptedToken: result.secureRegistrationLinkToken,
          userName: result.userName!,
          phoneNumber: payload.PhoneNumber!,
          eid: eid,
          prefillValuesForTesting: prefillValuesForTesting.toString(),
        }).toString();

        viaPathSsoUserName
          ? router.push(ROUTE_PATHS.TABLET.VIAPATH_SSO_API_ROUTE)
          : router.push(`/congrats?${queryString}`);
      } else {
        setNotQualified(true);
      }
    } catch (err) {
      toast({
        status: "error",
        title: "Error Occured",
        description: err.message,
      });
    }
  };

  useEffect(() => {
    const getTenant = async () => {
      try {
        const response = await handleServerAction(GetTenantByCode, {
          code: eid,
        });
        setTenant(response);
      } catch (err) {
        console.error(err);
      }
    };

    if (eid) {
      getTenant();
    }
  }, [eid]);

  const IsCurrentlyWorking = watch("IsCurrentlyWorking");
  const isReleaseDateUnknown = watch("EstimatedReleaseDate") === null;
  const DoYouHaveAnEmailAddress = watch("DoYouHaveAnEmailAddress");

  const showEmailUserNameField =
    (DoYouHaveAnEmailAddress !== null &&
      DoYouHaveAnEmailAddress !== undefined) ||
    !isJailUser;
  const isUserNameInsteadOfEmail = isJailUser
    ? !Boolean(DoYouHaveAnEmailAddress)
    : false;
  if (notQualified) {
    return <PrequalNotQualified isJailUser={isJailUser} />;
  }
  const reactSelectStyles = {
    container: (provided: any) => ({
      ...provided,
      width: "100%", // Width for the entire dropdown container
      maxWidth: "100%",
      borderColor: "#E2E8F0",
    }),
    control: (provided: any) => ({
      ...provided,
      borderColor: "#E2E8F0",
      width: "100%", // Width for the select box
      maxWidth: "100%",
    }),
    menu: (provided: any) => ({
      ...provided,
      width: "100%", // Width for the dropdown menu
      maxWidth: "100%",
    }),
  };

  return (
    <Box
      as="form"
      display="flex"
      flexDir="column"
      flex="1"
      gap="24px"
      p={["24px", "48px"]}
      h="100vh"
      overflowY="scroll"
      onSubmit={handleSubmit(submitDetails)}
    >
      {!id && (
        <VStack w="100%" gap="24px">
          <SectionHeader title="Contact Info" />
          {!viaPathSsoUserName && (
            <PrequalFormRow label="Full Name" fieldError={errors.FullName}>
              <Input
                type="text"
                placeholder="John Doe"
                size="md"
                {...register("FullName", { required: REQUIRED_FIELD })}
              />
            </PrequalFormRow>
          )}
          {isJailUser && !viaPathSsoUserName && (
            <PrequalFormRow
              label="Do you have an email address that you will be able to use and access after your release?"
              fieldError={errors.DoYouHaveAnEmailAddress}
            >
              <YesNoRadioGroup
                fieldName="DoYouHaveAnEmailAddress"
                control={control}
                onChangeCallback={() => {
                  setValue("Email", "");
                  setValue("UserName", "");
                }}
              />
            </PrequalFormRow>
          )}
          {showEmailUserNameField &&
            (isUserNameInsteadOfEmail ? (
              <PrequalFormRow label="Username" fieldError={errors.UserName}>
                <Input
                  type="text"
                  placeholder="johndoe"
                  size="md"
                  {...register("UserName", {
                    required: REQUIRED_FIELD,
                    pattern: validations.username,
                  })}
                />
              </PrequalFormRow>
            ) : (
              <PrequalFormRow label="Email Address" fieldError={errors.Email}>
                <Input
                  type="email"
                  placeholder="johndoe@gmail.com"
                  size="md"
                  {...register("Email", {
                    required: REQUIRED_FIELD,
                    pattern: validations.email,
                  })}
                />
              </PrequalFormRow>
            ))}
          <PrequalFormRow label="Phone Number" fieldError={errors.PhoneNumber}>
            <PhoneNumberInput register={register} />
          </PrequalFormRow>
          <PrequalFormRow label="Location" fieldError={errors.LocationId}>
            <Controller
              name="LocationId"
              control={control}
              rules={{ required: REQUIRED_FIELD }}
              render={({ field }) => (
                <LocationSelectReactSelect
                  value={field.value}
                  onChange={field.onChange}
                  styles={reactSelectStyles}
                />
              )}
            />
          </PrequalFormRow>
          <Divider bg="#CFCFCF" />
        </VStack>
      )}

      <SectionHeader
        title="General Eligibility"
        description="The government only offers Social Security Disability benefits to certain groups. Let’s check the basics first."
      />

      <PrequalFormRow
        label="Are you a United States citizen or permanent resident?"
        fieldError={errors.IsUsCitizen}
      >
        <YesNoRadioGroup fieldName="IsUsCitizen" control={control} />
      </PrequalFormRow>

      <PrequalFormRow label="Date of Birth" fieldError={errors.DateOfBirth}>
        <DateOfBirthInput register={register} />
      </PrequalFormRow>
      <PrequalFormRow
        label="Are you currently receiving any type of Social Security benefit?"
        helperText="Examples include SSDI, SSI, early retirement, or survivor benefits."
        fieldError={errors.IsReceivingGovtBenefits}
      >
        <YesNoRadioGroup
          fieldName="IsReceivingGovtBenefits"
          control={control}
        />
      </PrequalFormRow>
      <PrequalFormRow
        label="Do you owe money to the Social Security Administration for overpayments of disability benefits?"
        fieldError={errors.OwesSocialSecurityForOverpayments}
      >
        <YesNoRadioGroup
          fieldName="OwesSocialSecurityForOverpayments"
          control={control}
        />
      </PrequalFormRow>

      <PrequalFormRow
        label="Are you living with a physical or mental health condition?"
        fieldError={errors.HasPhysicalOrMentalHealthCondition}
      >
        <YesNoRadioGroup
          fieldName="HasPhysicalOrMentalHealthCondition"
          control={control}
        />
      </PrequalFormRow>

      <Divider bg="#CFCFCF" />
      {!isJailUser && (
        <>
          <SectionHeader
            title="Work History"
            description="Now let's cover your current work situation. Disability benefits
          will change based on how much you make and what your work history is."
          />

          <PrequalFormRow
            label="Are you currently working?"
            fieldError={errors.IsCurrentlyWorking}
          >
            <YesNoRadioGroup fieldName="IsCurrentlyWorking" control={control} />
          </PrequalFormRow>
          {IsCurrentlyWorking && (
            <PrequalFormRow
              label="What is the most hours you work in a week?"
              helperText="We need to know this because the government limits how much money you can make to receive disability benefits."
              fieldError={errors.MaximumHoursWorkedPerWeek}
            >
              <Input
                type="number"
                placeholder="40"
                size="md"
                {...register("MaximumHoursWorkedPerWeek", {
                  required: REQUIRED_FIELD,
                })}
              />
            </PrequalFormRow>
          )}
          {IsCurrentlyWorking && (
            <PrequalFormRow
              label="How much do you get paid per hour?"
              fieldError={errors.HourlyWage}
            >
              <InputGroup>
                <InputLeftElement pointerEvents="none">$</InputLeftElement>
                <Input
                  as={CurrencyInput}
                  decimalsLimit={2}
                  {...register("HourlyWage", {
                    required: REQUIRED_FIELD,
                  })}
                  placeholder="20"
                />
              </InputGroup>
            </PrequalFormRow>
          )}
          <Divider bg="#CFCFCF" />
          <PrequalFormRow
            label="How did you hear about us?"
            fieldError={errors.HowDidYouHearAboutUs}
          >
            <Controller
              name="HowDidYouHearAboutUs"
              control={control} // Pass control from useForm
              rules={{ required: "This field is required" }}
              render={({ field }) => (
                <Select
                  {...field}
                  options={[
                    { value: "Google Search", label: "Google Search" },
                    {
                      value: "In-Person Event or Webinar",
                      label: "In-Person Event or Webinar",
                    },
                    {
                      value: "Online Advertisement",
                      label: "Online Advertisement",
                    },
                    {
                      value: "Referral - Friend or Family",
                      label: "Referral - Friend or Family",
                    },
                    {
                      value: "Referral - Another Organization",
                      label: "Referral - Another Organization",
                    },
                    {
                      value: "Social Media (Facebook, Instagram, etc.)",
                      label: "Social Media (Facebook, Instagram, etc.)",
                    },
                    { value: "Other", label: "Other" },
                  ]}
                  placeholder="Choose One"
                  menuPlacement="top"
                  styles={reactSelectStyles}
                  onChange={(selectedOption) =>
                    field.onChange(selectedOption?.value)
                  } // Update value
                  value={
                    field.value
                      ? { value: field.value, label: field.value }
                      : null
                  } // Maintain selected value
                />
              )}
            />
          </PrequalFormRow>
        </>
      )}

      {(isJailUser || (tenant && tenant.isIncludeReleaseDate)) && (
        <PrequalFormRow
          label="When is your estimated release date?"
          fieldError={errors.EstimatedReleaseDate}
        >
          <Controller
            name="EstimatedReleaseDate"
            control={control}
            rules={{
              validate: (val) => {
                if (!val) {
                  if (val !== null) {
                    return REQUIRED_FIELD;
                  } else if (val == null) {
                    return true;
                  }
                }

                const selectedDate = moment(val, "YYYY-MM-DD");
                const today = moment().startOf("day");

                if (!selectedDate.isValid()) {
                  return "Invalid date format.";
                }

                if (selectedDate.isBefore(today)) {
                  return "The release date cannot be in the past.";
                }

                return true;
              },
            }}
            render={({ field }) => (
              <VStack alignItems="flex-end" w="100%">
                <Input
                  style={{
                    color:
                      field.value === placeHolderReleaseDate &&
                      !errors.EstimatedReleaseDate
                        ? "#718096" //placeholder styling
                        : undefined,
                  }}
                  type="date"
                  value={field.value || placeHolderReleaseDate}
                  isDisabled={field.value === null}
                  onChange={(ev) => {
                    field.onChange(ev.target.value);
                  }}
                  textTransform="uppercase"
                />
                <Checkbox
                  whiteSpace="nowrap"
                  isChecked={field.value === null}
                  onChange={(ev) =>
                    field.onChange(
                      ev.target.checked ? null : placeHolderReleaseDate
                    )
                  }
                >
                  I don&apos;t know.
                </Checkbox>
              </VStack>
            )}
          />
        </PrequalFormRow>
      )}
      {isJailUser && isReleaseDateUnknown && (
        <>
          <PrequalFormRow
            label="Is your release date in the next six months?"
            fieldError={errors.IsJailUserReleasedInNext6Months}
          >
            <Controller
              name="IsJailUserReleasedInNext6Months"
              control={control}
              rules={{ required: "This field is required" }}
              render={({ field }) => (
                <Select
                  {...field}
                  options={[
                    { value: "Yes", label: "Yes" },
                    { value: "No", label: "No" },
                    { value: "Unsure", label: "Unsure" },
                  ]}
                  placeholder="Choose One"
                  menuPlacement="top"
                  isSearchable={false}
                  menuPortalTarget={document.body}
                  styles={reactSelectStyles}
                  onChange={(selectedOption) =>
                    field.onChange(selectedOption?.value)
                  }
                  value={
                    field.value
                      ? { value: field.value, label: field.value }
                      : null
                  }
                />
              )}
            />
          </PrequalFormRow>
        </>
      )}
      {isJailUser && (
        <PrequalFormRow
          label="What is your Inmate ID?"
          helperText="This may be called your Prisoner ID or Booking Number."
          fieldError={errors.ExternalInmateId}
        >
          <Controller
            name="ExternalInmateId"
            control={control}
            rules={{
              validate: (val) => (val || val === null ? true : REQUIRED_FIELD),
            }}
            render={({ field }) => (
              <VStack alignItems="flex-end" w="100%">
                <Input
                  type="text"
                  placeholder="123321"
                  value={field.value || ""}
                  maxLength={24}
                  isDisabled={field.value === null}
                  onChange={(ev) => field.onChange(ev.target.value)}
                  textTransform="uppercase"
                />
                <Checkbox
                  whiteSpace="nowrap"
                  isChecked={field.value === null}
                  onChange={(ev) =>
                    field.onChange(ev.target.checked ? null : "")
                  }
                >
                  I don&apos;t know.
                </Checkbox>
              </VStack>
            )}
          />
        </PrequalFormRow>
      )}
      {isJailUser && (
        <TermsAndConditionsModal onTermsAcceptedChanged={setTermsAccepted} />
      )}

      <Box w="100%">
        <Button
          variant="primary"
          size="lg"
          fontWeight="500"
          w="100%"
          type="submit"
          isLoading={isSubmitting}
        >
          Submit Details
        </Button>
        <DuplicateEmailInlineAlert show={isDuplicateEmailAlertShowing} />
      </Box>
    </Box>
  );
};

export default PrequalForm;
